import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEURCompact } from "@/lib/api";
import { toast } from "sonner";
import { ListChecks, CheckCircle, Circle, CalendarBlank } from "@phosphor-icons/react";

const tomorrowIso = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
const fr = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });

function PlanItem({ it, onChanged }) {
  const [pick, setPick] = useState(false);
  const [until, setUntil] = useState(tomorrowIso());
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Action refusée"); } finally { setBusy(false); } };
  const toggle = () => run(() => api.post(it.done ? "/plan/uncomplete" : "/plan/complete", { action_id: it.action_id }), it.done ? "Action rouverte" : "Action marquée effectuée");
  const report = (d) => run(() => api.post("/commando/business/report", { action_id: it.action_id, until: d }), `Action reportée au ${fr(d)}`);
  return (
    <div className={`rounded-2xl border p-3 flex flex-wrap items-start gap-2 ${it.done ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"}`} data-testid={`cb-plan-item-${it.action_id}`}>
      <button onClick={toggle} disabled={busy} className="mt-0.5" aria-label="Marquer effectué" data-testid="cb-plan-toggle">
        {it.done ? <CheckCircle size={22} weight="fill" color="var(--cf-success)" /> : <Circle size={22} color="var(--cf-text-3)" />}
      </button>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-extrabold uppercase tracking-wide rounded-md px-1.5 py-0.5 ${it.source === "tresorerie" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`} data-testid="cb-plan-source">{it.source === "tresorerie" ? "Trésorerie" : "Objectif"}</span>
          <span className={`font-display text-sm font-extrabold text-slate-900 ${it.done ? "line-through" : ""}`} data-testid="cb-plan-nom">{it.nom}</span>
          <span className="text-[11px] font-bold text-slate-500">{it.delai}</span>
          <span className="text-[11px] font-bold text-slate-700 ml-auto">{formatEURCompact(it.ca_net)}</span>
        </div>
        <div className="text-xs text-slate-600 mt-0.5">{it.motif}</div>
        <div className="text-sm font-bold text-slate-900 mt-0.5" data-testid="cb-plan-action">→ {it.action}</div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Link to={`/dossier/${it.dossier_id}`} className="text-xs font-bold text-slate-700 underline" data-testid="cb-plan-open">Ouvrir le dossier</Link>
          {!it.done && <>
            <button onClick={() => report(tomorrowIso())} disabled={busy} className="text-xs font-bold text-slate-600 hover:text-slate-900" data-testid="cb-plan-report-tomorrow">Reporter à demain</button>
            <button onClick={() => setPick(!pick)} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:text-slate-900" data-testid="cb-plan-report-pick"><CalendarBlank size={12} /> Choisir une date</button>
            {pick && <>
              <input type="date" className="input-base py-0.5 w-40 text-xs" min={tomorrowIso()} value={until} onChange={(e) => setUntil(e.target.value)} data-testid="cb-plan-report-date" />
              <button onClick={() => report(until)} disabled={busy || !until} className="text-xs font-bold text-white bg-slate-900 rounded-md px-2 py-1" data-testid="cb-plan-report-confirm">Reporter</button>
            </>}
          </>}
        </div>
      </div>
    </div>
  );
}

export function PlanBlock({ plan, onChanged }) {
  const [showReported, setShowReported] = useState(false);
  const done = plan.items.filter((i) => i.done).length;
  const cancel = async (id) => { try { await api.delete(`/commando/business/report/${encodeURIComponent(id)}`); toast.success("Report annulé"); onChanged(); } catch { toast.error("Action refusée"); } };
  return (
    <section className="card-base p-5 md:p-6" data-testid="cb-plan">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks size={16} weight="fill" color="var(--cf-primary)" />
        <div className="overline" style={{ color: "var(--cf-primary)" }}>3 · Plan d'action du jour</div>
        <span className="ml-auto text-[11px] font-bold text-slate-500" data-testid="cb-plan-count">{done} / {plan.items.length} effectuée{done > 1 ? "s" : ""}</span>
      </div>
      {plan.items.length === 0 && <div className="text-sm text-slate-500" data-testid="cb-plan-empty">Aucune action prioritaire aujourd'hui.</div>}
      <div className="space-y-2">{plan.items.map((it) => <PlanItem key={it.action_id} it={it} onChanged={onChanged} />)}</div>
      {plan.reportees.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowReported(!showReported)} className="text-xs font-bold text-slate-500" data-testid="cb-plan-reported-toggle">{showReported ? "Masquer" : "Afficher"} les actions reportées ({plan.reportees.length})</button>
          {showReported && plan.reportees.map((it) => (
            <div key={it.action_id} className="flex items-center gap-2 text-xs text-slate-500 mt-1" data-testid={`cb-plan-reported-${it.action_id}`}>
              <span className="flex-1">{it.nom} — {it.action} · reportée au {fr(it.reported_until)}</span>
              <button onClick={() => cancel(it.action_id)} className="font-bold underline" data-testid="cb-plan-report-cancel">Annuler le report</button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-3" data-testid="cb-plan-note">{plan.note}</p>
    </section>
  );
}
